"use client"

/**
 * Canvas fields Enhance can change or confirm: format, difficulty, and Bloom’s.
 * Hosted in the Properties rail so the canvas stays stem, choices, and rationale.
 */

import * as React from "react"

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form"
import { Label } from "@/components/ui/label"
import { QuestionQualitySettingMark } from "@/components/question-quality/question-quality-setting-note"
import { LeoApplyField } from "@/components/question-quality/leo-apply-motion"
import {
  AUTHORING_QUESTION_TYPES,
  type AuthoringQuestionType,
} from "@/lib/library-authoring"
import {
  questionQualityHomeFor,
  type QuestionQualityCheck,
} from "@/lib/question-quality"

import {
  COMPOSER_FIELD_LABEL_CLASS,
  CategoryPickerControl,
  DifficultyMeter,
  FormatPickerControl,
  InspectorSubsectionLabel,
} from "./composer-fields"
import { useQuestionComposerContext } from "./composer-context"

function useQualitySettingMark() {
  const {
    disabled,
    qualityChecks,
    focusCheckId,
    applyQualityCheck,
    dismissQualityCheck,
    openQuestionQuality,
  } = useQuestionComposerContext()

  const focusedHome = React.useMemo(
    () => questionQualityHomeFor(qualityChecks, focusCheckId),
    [focusCheckId, qualityChecks]
  )

  return (
    check: QuestionQualityCheck | undefined,
    parts: { label: React.ReactNode; control: React.ReactNode },
    className?: string
  ) =>
    check ? (
      <QuestionQualitySettingMark
        check={check}
        label={parts.label}
        siblingChecks={qualityChecks}
        disabled={disabled}
        focused={focusedHome?.id === check.id}
        className={className}
        onUse={applyQualityCheck}
        onDismiss={(item) => dismissQualityCheck(item.id)}
        onOpenPane={(item) => openQuestionQuality(item.id)}
      >
        {parts.control}
      </QuestionQualitySettingMark>
    ) : (
      <div className={className ?? "flex flex-col gap-1.5"}>
        {parts.label}
        {parts.control}
      </div>
    )
}

export function ComposerFormatField() {
  const {
    form,
    capabilities,
    disabled,
    changeType,
    watchedType,
    qualityCheckForSetting,
  } = useQuestionComposerContext()
  const settingMark = useQualitySettingMark()

  const formatOptions = React.useMemo(
    () =>
      AUTHORING_QUESTION_TYPES.filter((option) => {
        if (option.value === watchedType) return true
        if (option.value === "numeric") return capabilities.numeric
        if (option.value === "fill_blank") return capabilities.fillBlank
        if (option.value === "matching") return capabilities.matchingPairs
        if (option.value === "ordering") return capabilities.ordering
        if (option.value === "hotspot") return capabilities.hotspot
        return true
      }),
    [capabilities, watchedType]
  )

  const formatCheck = qualityCheckForSetting("format")

  return (
    <FormField
      control={form.control}
      name="type"
      render={({ field }) => (
        <FormItem className="gap-1.5">
          {settingMark(formatCheck, {
            label: (
              <Label htmlFor="qb-format" className={COMPOSER_FIELD_LABEL_CLASS}>
                Question format
              </Label>
            ),
            control: (
              <FormControl>
                <LeoApplyField anchor="format">
                  <FormatPickerControl
                    id="qb-format"
                    options={formatOptions}
                    value={field.value}
                    onChange={(next: AuthoringQuestionType) => changeType(next)}
                    disabled={disabled}
                  />
                </LeoApplyField>
              </FormControl>
            ),
          })}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export function ComposerClassificationFields() {
  const {
    form,
    disabled,
    setDifficultyMode,
    difficultyInsight,
    watchedBloom,
    qualityCheckForSetting,
    setInspectorView,
  } = useQuestionComposerContext()
  const settingMark = useQualitySettingMark()
  const bloomCheck = qualityCheckForSetting("bloom")
  const difficultyCheck = qualityCheckForSetting("difficulty")

  return (
    <div id="composer-classification" className="flex flex-col gap-4">
      <FormField
        control={form.control}
        name="categoryIds"
        render={({ field }) => (
          <FormItem className="gap-1.5">
            <InspectorSubsectionLabel>Category</InspectorSubsectionLabel>
            <FormControl>
              <CategoryPickerControl
                value={field.value}
                onChange={field.onChange}
                disabled={disabled}
                onOpenCategories={() => setInspectorView("categories")}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="difficulty"
        render={({ field }) => (
          <DifficultyMeter
            value={field.value}
            onChange={(next) => {
              field.onChange(next)
              setDifficultyMode("manual")
            }}
            insight={difficultyInsight}
            bloom={watchedBloom}
            onBloomChange={(next) =>
              form.setValue("bloom", next, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            bloomMark={({ label, control }) =>
              settingMark(bloomCheck, {
                label,
                control: (
                  <LeoApplyField anchor="bloom">{control}</LeoApplyField>
                ),
              })
            }
            difficultyMark={({ label, control }) =>
              settingMark(difficultyCheck, {
                label,
                control: (
                  <LeoApplyField anchor="difficulty">{control}</LeoApplyField>
                ),
              })
            }
          />
        )}
      />
    </div>
  )
}
